import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { AlmaAccountCore } from "@almamedia/alma-account-core";

import { UserDTO } from "@/src/types/types";
import { sha256 } from "@/src/utils/cryptoUtils";
import {
  dispatchDataLayerEvent,
  generatePageID,
  generatePageInstanceID,
  getEnvironment,
  getLanguageCode,
  getPageTypeAndCategory,
  getViewport,
  isValidEmail,
  sanitizePathname,
  sanitizeTitle
} from "@/src/utils/digitalData";

const setUserLoggedOut = () => {
  globalThis.digitalData.user[0].segment.loginstatus = "loggedout";
  delete globalThis.digitalData.user[0].profile;
};

const updatePageData = async (
  sanitizedPathname: string,
  locale: string
): Promise<void> => {
  const {
    pageType,
    primaryCategory,
    subCategory1,
    subCategory2,
    subCategory3
  } = getPageTypeAndCategory(sanitizedPathname);

  const pageID = await generatePageID(sanitizedPathname);
  const environment = getEnvironment();
  const pageInstanceID = await generatePageInstanceID(pageID, environment);

  globalThis.digitalData.pageInstanceID = pageInstanceID;
  globalThis.digitalData.page.pageInfo.pageID = pageID;
  globalThis.digitalData.page.pageInfo.pageTitle = sanitizeTitle(
    document.title || ""
  );
  globalThis.digitalData.page.pageInfo.language = getLanguageCode(locale);
  globalThis.digitalData.page.pageInfo.locationPathName = sanitizedPathname;
  globalThis.digitalData.page.pageInfo.locationHost = globalThis.location.host;
  globalThis.digitalData.page.pageInfo.locationProtocol =
    globalThis.location.protocol.slice(0, -1);
  globalThis.digitalData.page.category.pageType = pageType;
  globalThis.digitalData.page.category.primaryCategory = primaryCategory;
  globalThis.digitalData.page.category.subCategory1 = subCategory1;
  globalThis.digitalData.page.category.subCategory2 = subCategory2;
  globalThis.digitalData.page.category.subCategory3 = subCategory3;
  globalThis.digitalData.page.attributes.viewport = getViewport(
    globalThis.innerWidth
  );
  globalThis.digitalData.page.attributes.width = globalThis.innerWidth;
};

const getUserProfileData = async (
  userEmail: string | undefined,
  userRole: unknown,
  userInfo: Record<string, unknown> | null | undefined
): Promise<{
  almaIDHash: string | undefined;
  emailHash: string | undefined;
  accountType: string;
}> => {
  const almaIDHash = userInfo?.["https://almamedia.net/almaIDHash"] as
    | string
    | undefined;
  const email = userEmail || (userInfo?.email as string | undefined) || "";

  let emailHash: string | undefined;
  if (email && isValidEmail(email)) {
    try {
      emailHash = await sha256(email);
    } catch (error) {
      console.warn("[DDL] Failed to hash email:", error);
    }
  }

  const accountType = typeof userRole === "string" ? userRole : "";

  return { almaIDHash, emailHash, accountType };
};

const updateUserData = async (
  isAuthenticated: boolean,
  user: unknown,
  userEmail: string | undefined,
  userRole: unknown
): Promise<void> => {
  if (!isAuthenticated || !user) {
    setUserLoggedOut();
    return;
  }

  try {
    const core = AlmaAccountCore.getInstance();
    const isAuth = await core.isAuthenticated();

    if (!isAuth) {
      setUserLoggedOut();
      return;
    }

    const userInfo = await core.getUserInfo();
    const { almaIDHash, emailHash, accountType } = await getUserProfileData(
      userEmail,
      userRole,
      userInfo
    );

    globalThis.digitalData.user[0].segment.loginstatus = "loggedin";
    globalThis.digitalData.user[0].profile ??= [{ profileInfo: {} }];
    globalThis.digitalData.user[0].profile[0].profileInfo = {
      almaIDHash,
      cdime: emailHash,
      accountType
    };
  } catch (error) {
    console.warn("[DDL] Failed to update user data:", error);
    setUserLoggedOut();
  }
};

export interface UseDigitalDataTrackerAuth {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  user: UserDTO | null;
}

export const useDigitalDataTracker = (
  locale: string,
  auth: UseDigitalDataTrackerAuth
) => {
  const location = useLocation();
  const { isAuthenticated, isAuthenticating, user } = auth;
  const previousPathnameRef = useRef<string>("");
  const previousAuthStateRef = useRef<boolean>(false);
  const isUpdatingRef = useRef<boolean>(false);

  // Extract stable values from user object
  const userId = user?.id;
  const userEmail = user?.email;
  const userRole = user?.role;

  useEffect(() => {
    let isMounted = true;

    const updateDigitalData = async () => {
      if (isUpdatingRef.current) return;
      if (isAuthenticating) return;
      isUpdatingRef.current = true;

      try {
        if (!globalThis.digitalData) return;

        const sanitizedPathname = sanitizePathname(location.pathname);
        await updatePageData(sanitizedPathname, locale);
        await updateUserData(isAuthenticated, user, userEmail, userRole);

        const pathnameChanged =
          previousPathnameRef.current !== sanitizedPathname;
        const authStateChanged =
          previousAuthStateRef.current !== isAuthenticated;

        if (isMounted && (pathnameChanged || authStateChanged)) {
          previousPathnameRef.current = sanitizedPathname;
          previousAuthStateRef.current = isAuthenticated;
          dispatchDataLayerEvent(globalThis.digitalData);
        }
      } catch (error) {
        console.error("[DDL] Failed to update digital data:", error);
      } finally {
        isUpdatingRef.current = false;
      }
    };

    updateDigitalData();

    return () => {
      isMounted = false;
    };
  }, [
    location.pathname,
    locale,
    isAuthenticated,
    isAuthenticating,
    userId,
    userEmail,
    userRole
  ]);
};
